// URL syntax in disclaimer text supported [[url,Terms of Service,https://shugarmansbath.com/terms-of-service/]]
// Now also supports escaped commas: [[url,Terms\, Conditions\, and Service,https://example.com]]
// Font Awesome icons: [[fa,calendar-days]] — to add a new icon, import it and add an entry to FA_ICON_MAP
// First name: [[first_name]] — swapped in from the `firstName` arg, blank if none is passed
// LeafGuard callback number: [[leaf_phone]] — swapped in from the `leafPhone` arg (see getLeafPhone
//   in style-data.js) as PLAIN TEXT, not a tel: link. Blank if unresolved, so an unconfigured org
//   renders nothing rather than leaking the raw token to a customer. Because the swap happens before
//   the tag pattern runs, it composes: [[b,[[leaf_phone]]]] renders the number in bold.

import { toTelFormat, toPlainFormat } from '../util/simple-phone-format';
import React from 'react';
import { FaCalendarDays } from 'react-icons/fa6';

// Add imports above and entries below as new icons are needed
const FA_ICON_MAP = {
  'calendar-days': FaCalendarDays
};

const replaceDateTags = (text) => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const endOfMonth = `${lastDay.getMonth() + 1}/${lastDay.getDate()}`;
  return text.replace(/\[\[endofmonth\]\]/gi, endOfMonth);
};

// Replacer is a function (not a string) so a firstName containing "$&" etc. isn't
// treated as a String.replace substitution pattern.
const replaceFirstNameTag = (text, firstName) => {
  return text.replace(/\[\[first_name\]\]/gi, () => firstName || '');
};

// Swaps in the number as plain text — deliberately NOT a [[phone,...]] rewrite, since this token
// renders as text rather than a tel: anchor. Running before the tag pattern is what lets the number
// be wrapped: [[b,[[leaf_phone]]]] becomes [[b,800-555-1234]] and bolds like any other text.
// Formatted through toPlainFormat so it matches how numbers render everywhere else in the UI.
// Replacer is a function for the same reason as replaceFirstNameTag.
// Because the value is substituted INTO the tag grammar, it is first stripped of the characters
// that grammar is built from. Without this, a configured "555-1234, ext 2" inside [[b,[[leaf_phone]]]]
// would split at the comma and render only the number, and a value containing [[...]] would be
// parsed as markup of its own. Phone numbers never need brackets, commas or backslashes.
const sanitizeLeafPhone = (value) =>
  String(value)
    .replace(/[[\],\\]/g, '')
    .trim();

export const replaceLeafPhoneTag = (text, leafPhone) => {
  const cleaned = leafPhone ? sanitizeLeafPhone(leafPhone) : '';
  const display = cleaned ? toPlainFormat(cleaned) : '';
  return text.replace(/\[\[leaf_phone\]\]/gi, () => display);
};

// Function to process the disclaimer text and return an array of strings and JSX elements
export const processDisclaimer = (disclaimerText, firstName, leafPhone) => {
  disclaimerText = replaceDateTags(disclaimerText);
  // Ahead of replaceFirstNameTag so a firstName that happens to contain [[leaf_phone]] can't
  // inject a phone number of its own.
  disclaimerText = replaceLeafPhoneTag(disclaimerText, leafPhone);
  disclaimerText = replaceFirstNameTag(disclaimerText, firstName);
  // Helper function to unescape text (removes backslashes before special characters)
  const unescapeText = (text) => {
    return text.replace(/\\(.)/g, '$1');
  };

  // Define the regex pattern to find url, phone, bold, italic, and fa icon patterns
  // Updated pattern: (?:[^,\\]|\\.)* matches either non-comma/non-backslash OR any escaped character
  const pattern = /\[\[(url|phone|b|i|fa),((?:[^,\\]|\\.)*?)(?:,((?:[^,\\]|\\.)*?))?\]\]/g;

  let parts = [];
  let lastIndex = 0;

  disclaimerText.replace(pattern, (match, type, textOrNumber, url, index) => {
    // Push the text before the match
    parts.push(disclaimerText.slice(lastIndex, index));

    // Unescape the captured text to convert \, back to ,
    textOrNumber = unescapeText(textOrNumber);
    if (url) url = unescapeText(url);

    if (type === 'url') {
      parts.push(
        <a href={url} key={url} target="_blank" rel="noreferrer">
          {textOrNumber}
        </a>
      );
    } else if (type === 'phone') {
      const telFormat = toTelFormat(textOrNumber);
      const regFormat = toPlainFormat(textOrNumber);
      parts.push(
        <a href={`tel:${telFormat}`} key={textOrNumber}>
          {regFormat}
        </a>
      );
    } else if (type === 'b') {
      parts.push(<b key={`b-${index}`}>{textOrNumber}</b>);
    } else if (type === 'i') {
      parts.push(<i key={`i-${index}`}>{textOrNumber}</i>);
    } else if (type === 'fa') {
      const Icon = FA_ICON_MAP[textOrNumber.trim()];
      if (Icon) {
        parts.push(<Icon key={`fa-${index}`} style={{ verticalAlign: 'middle' }} />);
      } else if (process.env.NODE_ENV !== 'production') {
        console.warn(
          `[processDisclaimer] Unknown FA icon: "${textOrNumber.trim()}" — import it and add it to FA_ICON_MAP in process-disclaimer.js`
        );
      }
    }

    lastIndex = index + match.length;
  });

  // Push the remaining text after the last match
  parts.push(disclaimerText.slice(lastIndex));

  // Additional processing to handle \\n
  parts = parts.flatMap((part) => {
    if (typeof part === 'string') {
      return part.split('\\n').reduce((acc, segment, index, array) => {
        acc.push(segment);
        if (index < array.length - 1) acc.push(<div key={`br-${index}`} style={{ height: '4px' }} />);
        return acc;
      }, []);
    } else {
      return part;
    }
  });

  return parts;
};
